import { z } from "zod";

// Per-day auto-tune savings point from
// `GET /jobs/{job_id}/auto-tune/savings-trends`. ``dbu_savings`` and
// ``infra_savings`` are the two components of the day's savings; the
// chart stacks them and the tooltip breaks them out.
export const AutoTuneSavingsTrendPointSchema = z.object({
  period: z.string(),
  dbu_savings: z.number().default(0),
  infra_savings: z.number().default(0),
});

export type AutoTuneSavingsTrendPoint = z.infer<typeof AutoTuneSavingsTrendPointSchema>;

// Mirrors the BE ``AutoTuneSavingsTrendsResponse``. ``actual`` is the
// realized savings on runs that had an applied recommendation in effect;
// ``potential`` is what those same runs would have saved from the active
// recommendation. Each series is bucketed by the run window.
export const AutoTuneSavingsTrendsResponseSchema = z.object({
  actual: z.array(AutoTuneSavingsTrendPointSchema).default([]),
  potential: z.array(AutoTuneSavingsTrendPointSchema).default([]),
  granularity: z.string().default("day"),
});

export type AutoTuneSavingsTrendsResponse = z.infer<typeof AutoTuneSavingsTrendsResponseSchema>;

// FE-derived point for the single stacked chart, mirroring the Snowflake
// warehouse auto-manage graph: per day we stack potential savings,
// realized (auto-tune) savings, and the actual run cost (spend). Realized
// and potential each carry their DBU/infra split for the tooltip. Built
// client-side by merging the savings ``actual``/``potential`` series with
// the job's per-day cost on ``period``.
export interface AutoTuneSavingsStackedPoint {
  period: string;
  realized: number;
  potential: number;
  spend: number;
  realizedDbu: number;
  realizedInfra: number;
  potentialDbu: number;
  potentialInfra: number;
}

// Mirrors the BE ``AutoTuneHistoryEvent`` Pydantic schema produced by
// `GET /jobs/{job_id}/auto-tune/history`. The BE composes
// ``event_message`` server-side via ``DBX_AUTO_TUNE_EVENT_UI_MAPPING``
// so the FE renders one string verbatim and only needs to pick the
// icon based on ``event_type`` × ``mode``.
// The enum spans every real BE ``event_type`` across jobs and cluster
// Auto Tune (jobs emits a subset). Listing them all keeps each one a
// valid history filter value — e.g. ``BACKOFF`` — instead of a value the
// UI silently drops.
export const AutoTuneHistoryEventTypeSchema = z.enum([
  "APPLY_REQUESTED",
  "MUTATION_STARTED",
  "APPLIED",
  "BACKOFF",
  "REVERTED",
  "FAILED",
  "CANCELLED",
]);

// Human-readable labels for each history ``event_type``, in display
// order, driving the history event-type filter. The raw enum value is
// what the BE ``event_types`` query param expects; the label is UI-only.
export const AUTO_TUNE_HISTORY_EVENT_TYPE_OPTIONS: {
  value: z.infer<typeof AutoTuneHistoryEventTypeSchema>;
  label: string;
}[] = [
  { value: "APPLY_REQUESTED", label: "Enable requested" },
  { value: "MUTATION_STARTED", label: "Applying" },
  { value: "APPLIED", label: "Applied" },
  { value: "BACKOFF", label: "Auto rolled back" },
  { value: "REVERTED", label: "Disabled" },
  { value: "FAILED", label: "Failed" },
  { value: "CANCELLED", label: "Cancelled" },
];

export const AutoTuneHistoryEventSchema = z.object({
  event_id: z.number(),
  event_type: AutoTuneHistoryEventTypeSchema,
  mode: z.string().nullable().optional(),
  change_timestamp: z.string(),
  task_key: z.string().nullable().optional(),
  recommendation_from: z.string().nullable().optional(),
  recommendation_to: z.string().nullable().optional(),
  changed_by: z.number().nullable().optional(),
  changed_by_name: z.string().nullable().optional(),
  actionable_message: z.string().nullable().optional(),
  reason: z.string().nullable().optional(),
  event_message: z.string(),
});

export const AutoTuneHistoryResponseSchema = z.object({
  items: z.array(AutoTuneHistoryEventSchema),
  // Pagination shape mirrors the BE Pydantic model
  // (``total_count`` / ``page`` / ``page_size`` / ``total_pages``).
  // Older responses used ``total`` / ``size`` / ``pages``; aliases
  // remain for back-compat against the storybook fixtures.
  total_count: z.number().default(0),
  page: z.number().default(1),
  page_size: z.number().default(10),
  total_pages: z.number().default(1),
  total: z.number().default(0).optional(),
  size: z.number().default(10).optional(),
  pages: z.number().default(1).optional(),
});

export const AutoTuneStatusPreflightSchema = z.object({
  blocked: z.boolean().default(false),
  reason: z.string().nullable().optional(),
  actionable_message: z.string().nullable().optional(),
});

export const AutoTuneStatusResponseSchema = z.object({
  auto_tune_mode: z.string().nullable().optional(),
  preflight: AutoTuneStatusPreflightSchema.default({ blocked: false }),
  recommendation_count: z.number().default(0),
  tasks_enabled: z.number().default(0),
  tasks_total: z.number().default(0),
  at_scoped_spend_usd: z.number().nullable().optional(),
});

export type AutoTuneStatusResponse = z.infer<typeof AutoTuneStatusResponseSchema>;

export const AutoTuneSettingsScopeSchema = z.enum(["all_tasks", "specific_tasks"]);

export const AutoTuneSettingsSchema = z.object({
  enabled: z.boolean().default(false),
  scope: AutoTuneSettingsScopeSchema.default("all_tasks"),
  enabled_task_keys: z.array(z.string()).default([]),
  reasoning: z.string().nullable().optional(),
});

export type AutoTuneHistoryEventType = z.infer<typeof AutoTuneHistoryEventTypeSchema>;
export type AutoTuneHistoryEvent = z.infer<typeof AutoTuneHistoryEventSchema>;
export type AutoTuneHistoryResponse = z.infer<typeof AutoTuneHistoryResponseSchema>;
export type AutoTuneSettingsScope = z.infer<typeof AutoTuneSettingsScopeSchema>;
export type AutoTuneSettings = z.infer<typeof AutoTuneSettingsSchema>;
